import { inject, Service } from '@angular/core';
import { DataAccessCorporateDataService } from '@h5s/data-access-corporate-data';
import { DataAccessDepartmentService } from '@h5s/data-access-settings';
import { DialogService, NavigationService, ToastService } from '@h5s/design-system';
import {
  CashRegisterMode,
  City,
  CityTaxVatType,
  CreateOwnerCorporateData,
  DefaultVatChargeability,
  EInvoiceManagerProvider,
  EInvoicingMode,
  FiscalSystem,
  LiquidationProcess,
  OwnerCorporateData,
  State,
  UpdateOwnerCorporateData,
} from '@h5s/entities';
import { LocationService } from '@h5s/location';
import { isAppErrorWithBackendCause, showErrorAsToast } from '@h5s/util-error';
import { ActionType } from '@h5s/util-overlay';
import { catchError, lastValueFrom, map, Observable, of, tap } from 'rxjs';
import { FEATURE_CORPORATE_DATA_TRANSLATIONS } from './feature-corporate-data/translations/feature-corporate-data.translations';

export const EInvoicingModeOptions: Record<EInvoicingMode, string> = {
  [EInvoicingMode.None]: FEATURE_CORPORATE_DATA_TRANSLATIONS.eInvoicingModeNone,
  [EInvoicingMode.AutoSend]: FEATURE_CORPORATE_DATA_TRANSLATIONS.eInvoicingModeAutoSend,
  [EInvoicingMode.Export]: FEATURE_CORPORATE_DATA_TRANSLATIONS.eInvoicingModeExport,
  [EInvoicingMode.ManualSend]: FEATURE_CORPORATE_DATA_TRANSLATIONS.eInvoicingModeManualSend,
};

export const EInvoiceManagerProviderOptions: Record<EInvoiceManagerProvider, string> = {
  [EInvoiceManagerProvider.Systems]: FEATURE_CORPORATE_DATA_TRANSLATIONS.eInvoiceManagerProviderSystems,
  [EInvoiceManagerProvider.DigitalHubZucchetti]:
    FEATURE_CORPORATE_DATA_TRANSLATIONS.eInvoiceManagerProviderDigitalHubZucchetti,
  [EInvoiceManagerProvider.DocEasy]: FEATURE_CORPORATE_DATA_TRANSLATIONS.eInvoiceManagerProviderDocEasy,
  [EInvoiceManagerProvider.FattureInCloud]: FEATURE_CORPORATE_DATA_TRANSLATIONS.eInvoiceManagerProviderFattureInCloud,
};

export const FiscalSystemOptions: Record<FiscalSystem, string> = {
  [FiscalSystem.Ordinary]: FEATURE_CORPORATE_DATA_TRANSLATIONS.fiscalSystemOrdinary,
  [FiscalSystem.Farmhouse]: FEATURE_CORPORATE_DATA_TRANSLATIONS.fiscalSystemFarmhouse,
  [FiscalSystem.Flat]: FEATURE_CORPORATE_DATA_TRANSLATIONS.fiscalSystemFlat,
  [FiscalSystem.Agriculture]: FEATURE_CORPORATE_DATA_TRANSLATIONS.fiscalSystemAgriculture,
  [FiscalSystem.Tourism]: FEATURE_CORPORATE_DATA_TRANSLATIONS.fiscalSystemTourism,
  [FiscalSystem.Other]: FEATURE_CORPORATE_DATA_TRANSLATIONS.fiscalSystemOther,
};

export const DefaultVatChargeabilityOptions: Record<DefaultVatChargeability, string> = {
  [DefaultVatChargeability.Immediate]: FEATURE_CORPORATE_DATA_TRANSLATIONS.defaultVatChargeabilityImmediate,
  [DefaultVatChargeability.Deferred]: FEATURE_CORPORATE_DATA_TRANSLATIONS.defaultVatChargeabilityDeferred,
  [DefaultVatChargeability.SplitPayment]: FEATURE_CORPORATE_DATA_TRANSLATIONS.defaultVatChargeabilitySplitPayment,
};

export const CashRegisterModeOptions: Record<CashRegisterMode, string> = {
  [CashRegisterMode.None]: FEATURE_CORPORATE_DATA_TRANSLATIONS.cashRegisterModeNone,
  [CashRegisterMode.FiscalExternal]: FEATURE_CORPORATE_DATA_TRANSLATIONS.cashRegisterModeFiscalExternal,
  [CashRegisterMode.FiscalReceipt]: FEATURE_CORPORATE_DATA_TRANSLATIONS.cashRegisterModeFiscalReceipt,
  [CashRegisterMode.NotFiscal]: FEATURE_CORPORATE_DATA_TRANSLATIONS.cashRegisterModeNotFiscal,
};

export const LiquidationProcessOptions: Record<LiquidationProcess, string> = {
  [LiquidationProcess.NotInLiquidation]: FEATURE_CORPORATE_DATA_TRANSLATIONS.liquidationProcessNotInLiquidation,
  [LiquidationProcess.InLiquidation]: FEATURE_CORPORATE_DATA_TRANSLATIONS.liquidationProcessInLiquidation,
};

export const CityTaxVatTypeOptions: Record<CityTaxVatType, string> = {
  [CityTaxVatType.N1]: FEATURE_CORPORATE_DATA_TRANSLATIONS.cityTaxVatTypeN1,
  [CityTaxVatType.N2]: FEATURE_CORPORATE_DATA_TRANSLATIONS.cityTaxVatTypeN2,
  [CityTaxVatType.N2_1]: FEATURE_CORPORATE_DATA_TRANSLATIONS.cityTaxVatTypeN21,
  [CityTaxVatType.N2_2]: FEATURE_CORPORATE_DATA_TRANSLATIONS.cityTaxVatTypeN22,

  [CityTaxVatType.N3]: FEATURE_CORPORATE_DATA_TRANSLATIONS.cityTaxVatTypeN3,
  [CityTaxVatType.N3_1]: FEATURE_CORPORATE_DATA_TRANSLATIONS.cityTaxVatTypeN31,
  [CityTaxVatType.N3_2]: FEATURE_CORPORATE_DATA_TRANSLATIONS.cityTaxVatTypeN32,
  [CityTaxVatType.N3_3]: FEATURE_CORPORATE_DATA_TRANSLATIONS.cityTaxVatTypeN33,
  [CityTaxVatType.N3_4]: FEATURE_CORPORATE_DATA_TRANSLATIONS.cityTaxVatTypeN34,
  [CityTaxVatType.N3_5]: FEATURE_CORPORATE_DATA_TRANSLATIONS.cityTaxVatTypeN35,
  [CityTaxVatType.N3_6]: FEATURE_CORPORATE_DATA_TRANSLATIONS.cityTaxVatTypeN36,

  [CityTaxVatType.N4]: FEATURE_CORPORATE_DATA_TRANSLATIONS.cityTaxVatTypeN4,
  [CityTaxVatType.N5]: FEATURE_CORPORATE_DATA_TRANSLATIONS.cityTaxVatTypeN5,

  [CityTaxVatType.N6]: FEATURE_CORPORATE_DATA_TRANSLATIONS.cityTaxVatTypeN6,
  [CityTaxVatType.N6_1]: FEATURE_CORPORATE_DATA_TRANSLATIONS.cityTaxVatTypeN61,
  [CityTaxVatType.N6_2]: FEATURE_CORPORATE_DATA_TRANSLATIONS.cityTaxVatTypeN62,
  [CityTaxVatType.N6_3]: FEATURE_CORPORATE_DATA_TRANSLATIONS.cityTaxVatTypeN63,
  [CityTaxVatType.N6_4]: FEATURE_CORPORATE_DATA_TRANSLATIONS.cityTaxVatTypeN64,
  [CityTaxVatType.N6_5]: FEATURE_CORPORATE_DATA_TRANSLATIONS.cityTaxVatTypeN65,
  [CityTaxVatType.N6_6]: FEATURE_CORPORATE_DATA_TRANSLATIONS.cityTaxVatTypeN66,
  [CityTaxVatType.N6_7]: FEATURE_CORPORATE_DATA_TRANSLATIONS.cityTaxVatTypeN67,
  [CityTaxVatType.N6_8]: FEATURE_CORPORATE_DATA_TRANSLATIONS.cityTaxVatTypeN68,
  [CityTaxVatType.N6_9]: FEATURE_CORPORATE_DATA_TRANSLATIONS.cityTaxVatTypeN69,

  [CityTaxVatType.N7]: FEATURE_CORPORATE_DATA_TRANSLATIONS.cityTaxVatTypeN7,
};

@Service()
export class FeatureCorporateDataService {
  private readonly dataAccessCorporateDataService = inject(DataAccessCorporateDataService);
  private readonly toastService = inject(ToastService);
  private readonly dialogService = inject(DialogService);
  private readonly locationService = inject(LocationService);
  private readonly navigationService = inject(NavigationService);
  private readonly dataAccessDepartmentService = inject(DataAccessDepartmentService);

  /* Translations */
  private readonly translation = FEATURE_CORPORATE_DATA_TRANSLATIONS;

  /**
   * Recupera l'anagrafica fiscale
   */
  public getCorporateData(corporateDataId: number): Observable<OwnerCorporateData> {
    return this.dataAccessCorporateDataService.getCorporateData(corporateDataId);
  }

  /**
   * Crea l'anagrafica fiscale
   * @param corporateData
   */
  public createOwnerCorporateData(createOwnerCorporateData: CreateOwnerCorporateData): Observable<number> {
    return this.dataAccessCorporateDataService.createOwnerCorporateData(createOwnerCorporateData).pipe(
      tap(() => {
        this.toastService.showSuccess(this.translation.toastSavedCorporateData);
      }),
      showErrorAsToast(this.translation.toastUpsertCorporateDataError),
    );
  }

  /**
   * Modifica l'anagrafica fiscale
   * @param corporateData
   */
  public updateOwnerCorporateData(updateOwnerCorporateData: UpdateOwnerCorporateData): Observable<boolean> {
    return this.dataAccessCorporateDataService.updateOwnerCorporateData(updateOwnerCorporateData).pipe(
      tap(() => {
        this.toastService.showSuccess();
      }),
      showErrorAsToast(this.translation.toastUpsertCorporateDataError),
    );
  }

  /**
   * Elimina l'anagrafica fiscale
   * @param corporateDataId
   */
  public deleteOwnerCorporateData(corporateDataId: number): Observable<boolean> {
    return this.dataAccessCorporateDataService.deleteOwnerCorporateData(corporateDataId).pipe(
      tap(() => {
        this.toastService.showSuccess(this.translation.toastDeleteCorporateDataOk);
      }),
      showErrorAsToast(this.translation.toastDeleteCorporateDataError),
    );
  }

  /**
   * Test credenziali eInvoiceManager
   */
  public checkLoginElectronicInvoice(corporateData: UpdateOwnerCorporateData): Observable<boolean> {
    return this.dataAccessCorporateDataService.checkLoginElectronicInvoice(corporateData).pipe(
      tap(() => {
        this.toastService.showSuccess(this.translation.toastTestEInvoiceCredentialsOk);
      }),
      catchError((error) => {
        const errorMessage = isAppErrorWithBackendCause(error)
          ? this.translation.dialogTestEInvoiceCredentialsErrorContent
          : this.translation.dialogTestEInvoiceCredentialsGeneralError;

        this.dialogService.showAlert({
          content: errorMessage,
        });

        return of(false);
      }),
    );
  }

  public dialogDeleteConfirmation(): Promise<boolean> {
    return lastValueFrom(
      this.dialogService
        .showDelete({
          content: this.translation.dialogDeleteConfirmationCorporateData,
        })
        .pipe(map((result) => result.actionType === ActionType.Confirm)),
    );
  }

  /**
   * Naviga verso l'anagrafica fiscale indicata
   */
  public navigateToCorporateData(corporateDataId: number | null, reuseTab?: boolean): void {
    if (corporateDataId) {
      this.navigationService.navigate(`/corporate-data/edit/${corporateDataId}`, {
        replaceUrl: reuseTab,
        state: { reuseTab },
      });
    }
  }

  // Restituisce la lista degli stati disponibili
  public getStateRecord(): Observable<Record<number, string>> {
    return this.locationService.getStatesRecord();
  }

  // Restituisce lo stato a partire dall'id
  public getStateById(id: number | null): Observable<State | undefined> {
    if (!id) return of(undefined);

    return this.locationService.getStateById(id);
  }

  // Restituisce la città a partire dall'id
  public getCityById(id: number): Observable<City> {
    return this.locationService.getCityById(id);
  }

  // Restituisce la lista dei dipartimenti disponibili
  public getDepartmentRecord(): Observable<Record<number, string>> {
    return this.dataAccessDepartmentService.getAllDepartment().pipe(
      map((departments) =>
        departments.reduce(
          (acc, department) => {
            acc[department.id] = department.description;
            return acc;
          },
          {} as Record<number, string>,
        ),
      ),
    );
  }

  // Restituisce ownerCorporateData a partire dal block
  public resolveOwnerCorporateData(block: string | null): Observable<OwnerCorporateData> {
    return this.dataAccessCorporateDataService.resolveOwnerCorporateData(block);
  }
}
